import { bus } from '@/core/EventBus';
import type { GameState, RelationshipId, ReputationTrack } from '@/gameplay/GameState';

export interface DialogueEffect {
  readonly money?: number;
  readonly reputation?: Partial<Record<ReputationTrack, number>>;
  readonly relationship?: Partial<Record<RelationshipId | string, number>>;
  readonly flag?: string;
  readonly flags?: Record<string, boolean>;
}

export interface DialogueChoice {
  readonly label: string;
  readonly next?: string;
  readonly effects?: readonly DialogueEffect[];
}

export interface DialogueNode {
  readonly id: string;
  readonly speaker?: string;
  readonly text: string;
  readonly next?: string;
  readonly effects?: readonly DialogueEffect[];
  readonly choices?: readonly DialogueChoice[];
}

export interface DialogueDefinition {
  readonly id: string;
  readonly start: string;
  readonly nodes: readonly DialogueNode[];
}

export interface DialogueSnapshot {
  readonly active: boolean;
  readonly dialogueId: string | null;
  readonly node: DialogueNode | null;
}

export class DialogueRunner {
  readonly #state: GameState;
  readonly #nodes = new Map<string, DialogueNode>();
  #definition: DialogueDefinition | null = null;
  #node: DialogueNode | null = null;
  #appliedNodeEffects = new Set<string>();

  constructor(state: GameState) {
    this.#state = state;
  }

  get active(): boolean {
    return this.#definition !== null;
  }

  get currentNode(): DialogueNode | null {
    return this.#node;
  }

  start(definition: DialogueDefinition): void {
    this.#nodes.clear();
    this.#appliedNodeEffects.clear();
    for (const node of definition.nodes) {
      if (this.#nodes.has(node.id)) throw new Error(`Duplicate dialogue node "${node.id}"`);
      this.#nodes.set(node.id, node);
    }
    const start = this.#requireNode(definition.start);
    this.#definition = definition;
    this.#enterNode(start);
    bus.emit('dialogue:started', { id: definition.id, nodeId: start.id });
  }

  continue(): void {
    if (!this.#definition || !this.#node) return;
    if (this.#node.choices && this.#node.choices.length > 0) return;
    if (!this.#node.next) {
      this.end();
      return;
    }
    this.#enterNode(this.#requireNode(this.#node.next));
  }

  choose(index: number): void {
    if (!this.#definition || !this.#node) return;
    const choices = this.#node.choices ?? [];
    const choice = choices[index];
    if (!choice) throw new Error(`Dialogue choice ${index} does not exist on node "${this.#node.id}"`);
    this.#applyEffects(choice.effects ?? []);
    bus.emit('dialogue:choiceSelected', {
      id: this.#definition.id,
      nodeId: this.#node.id,
      choiceIndex: index,
      label: choice.label,
    });
    if (!choice.next) {
      this.end();
      return;
    }
    this.#enterNode(this.#requireNode(choice.next));
  }

  end(): void {
    const id = this.#definition?.id;
    if (!id) return;
    this.#definition = null;
    this.#node = null;
    this.#nodes.clear();
    this.#appliedNodeEffects.clear();
    bus.emit('dialogue:ended', { id });
  }

  snapshot(): DialogueSnapshot {
    return {
      active: this.active,
      dialogueId: this.#definition?.id ?? null,
      node: this.#node,
    };
  }

  #enterNode(node: DialogueNode): void {
    this.#node = node;
    if (!this.#appliedNodeEffects.has(node.id)) {
      this.#applyEffects(node.effects ?? []);
      this.#appliedNodeEffects.add(node.id);
    }
    if (this.#definition) bus.emit('dialogue:nodeChanged', { id: this.#definition.id, nodeId: node.id });
  }

  #requireNode(id: string): DialogueNode {
    const node = this.#nodes.get(id);
    if (!node) throw new Error(`Dialogue node "${id}" does not exist`);
    return node;
  }

  #applyEffects(effects: readonly DialogueEffect[]): void {
    for (const effect of effects) {
      if (effect.money !== undefined) {
        if (effect.money >= 0) this.#state.earn(effect.money);
        else this.#state.spend(Math.abs(effect.money));
      }
      for (const [track, delta] of Object.entries(effect.reputation ?? {}) as Array<[ReputationTrack, number]>) {
        this.#state.changeReputation(track, delta);
      }
      for (const [id, delta] of Object.entries(effect.relationship ?? {}) as Array<[string, number]>) {
        this.#state.changeRelationship(id, delta);
      }
      if (effect.flag) this.#state.setFlag(effect.flag);
      for (const [id, value] of Object.entries(effect.flags ?? {})) this.#state.setFlag(id, value);
    }
  }
}
